import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { t } from "@/i18n/sk";
import { getProducts, getExpansions, getEras } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { HoloBackground } from "@/components/HoloBackground";

const MODES = {
  etb: { title: t.nav.etb, eyebrow: "ETB boxy", intro: "Elite Trainer Boxy naprieč všetkými sériami.", types: ["etb"] },
  boosters: { title: t.nav.boosters, eyebrow: "Boostre", intro: "Booster packy a booster boxy na otváranie.", types: ["booster_box", "booster_pack"] },
  news: { title: t.newArrivals.title, eyebrow: "Novinky", intro: "Najnovšie prírastky do našej ponuky.", isNew: true },
  search: { title: "Vyhľadávanie", eyebrow: "Katalóg", intro: "" },
};

export default function CatalogPage({ mode }) {
  const cfg = MODES[mode];
  const [params] = useSearchParams();
  const q = params.get("q") || "";

  const [products, setProducts] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const [eras, setEras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eraFilter, setEraFilter] = useState("all");
  const [expansionFilter, setExpansionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState("default");

  useEffect(() => {
    getExpansions().then(setExpansions).catch(() => {});
    getEras().then(setEras).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    if (cfg.types) query.types = cfg.types.join(",");
    if (cfg.isNew) query.is_new = true;
    if (mode === "search" && q) query.q = q;
    getProducts(query).then(setProducts).catch(() => setProducts([])).finally(() => setLoading(false));
    setEraFilter("all");
    setExpansionFilter("all");
    setTypeFilter("all");
  }, [mode, q]);

  const availableTypes = cfg.types || ["etb", "booster_box", "booster_pack"];
  const expEraMap = useMemo(() => Object.fromEntries(expansions.map((e) => [e.slug, e.era_slug])), [expansions]);
  const seriesOptions = eraFilter === "all" ? expansions : expansions.filter((e) => e.era_slug === eraFilter);

  const filtered = useMemo(() => {
    let list = [...products];
    if (eraFilter !== "all") list = list.filter((p) => expEraMap[p.expansion_slug] === eraFilter);
    if (expansionFilter !== "all") list = list.filter((p) => p.expansion_slug === expansionFilter);
    if (typeFilter !== "all") list = list.filter((p) => p.product_type === typeFilter);
    if (sort === "price-asc") list.sort((a, b) => a.price_eur - b.price_eur);
    if (sort === "price-desc") list.sort((a, b) => b.price_eur - a.price_eur);
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, eraFilter, expansionFilter, typeFilter, sort, expEraMap]);

  const title = mode === "search" ? (q ? t.catalog.searchTitle(q) : cfg.title) : cfg.title;
  const selectClass = "rounded-lg border border-white/10 bg-vault-card px-3 py-2 text-sm text-white focus:border-cyan-glow/50 focus:outline-none";

  return (
    <div className="vault-bg">
      <section className="relative overflow-hidden border-b border-white/8">
        <HoloBackground />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="overline mb-3 block">{cfg.eyebrow}</span>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{title}</h1>
          {cfg.intro && <p className="mt-4 max-w-2xl text-slate-300">{cfg.intro}</p>}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-300"><SlidersHorizontal className="h-4 w-4 text-cyan-300" /> {t.catalog.filters}</span>
          {eras.length > 0 && (
            <select data-testid="filter-era" value={eraFilter} onChange={(e) => { setEraFilter(e.target.value); setExpansionFilter("all"); }} className={selectClass}>
              <option value="all">Všetky éry</option>
              {eras.map((er) => <option key={er.slug} value={er.slug}>{er.name}</option>)}
            </select>
          )}
          <select data-testid="filter-expansion" value={expansionFilter} onChange={(e) => setExpansionFilter(e.target.value)} className={selectClass}>
            <option value="all">{t.catalog.allSeries}</option>
            {seriesOptions.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}
          </select>
          {availableTypes.length > 1 && (
            <select data-testid="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectClass}>
              <option value="all">{t.catalog.allTypes}</option>
              {availableTypes.map((ty) => <option key={ty} value={ty}>{t.productTypes[ty]}</option>)}
            </select>
          )}
          <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={selectClass}>
            <option value="default">{t.catalog.sort}</option>
            <option value="price-asc">Cena: najnižšia</option>
            <option value="price-desc">Cena: najvyššia</option>
            <option value="name">Názov A–Z</option>
          </select>
          <span className="ml-auto text-sm text-slate-400">{t.catalog.results(filtered.length)}</span>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-500">Načítavam…</div>
        ) : filtered.length === 0 ? (
          <p className="py-20 text-center text-slate-400">{t.catalog.noResults}</p>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
